import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { apiService } from './services/api';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Billing from './components/Billing';
import Admin from './components/Admin';
import Account from './components/Account';
import { feLogger } from './utils/logger';

function RouteLogger() {
  const location = useLocation();
  useEffect(() => {
    feLogger.info('route_change', { pathname: location.pathname, search: location.search });
  }, [location]);
  return null;
}

function App() {
  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      feLogger.error('unhandled_error', event.error || event.message);
    };
    const rejHandler = (event: PromiseRejectionEvent) => {
      feLogger.error('unhandled_promise_rejection', event.reason);
    };
    window.addEventListener('error', handler);
    window.addEventListener('unhandledrejection', rejHandler);
    return () => {
      window.removeEventListener('error', handler);
      window.removeEventListener('unhandledrejection', rejHandler);
    };
  }, []);

  return (
    <Router>
      <div className="App">
        <RouteLogger />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/billing/success" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/billing/fail" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

// Защищенный роут — только для авторизованных.
// Запоминаем, куда человек шёл, чтобы после входа вернуть его туда же
// (иначе прямая ссылка на /billing выглядела бы как «не открывается»).
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = apiService.isAuthenticated();

  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children}</>;
};

// Админский роут — только для is_admin. Не-админа возвращаем в его кабинет.
// Это защита на клиенте (данные и так закрыты 403 на бэкенде).
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<'loading' | 'ok' | 'deny'>('loading');

  useEffect(() => {
    if (!apiService.isAuthenticated()) {
      setState('deny');
      return;
    }
    apiService
      .getMe()
      .then((me) => setState(me.is_admin ? 'ok' : 'deny'))
      .catch(() => setState('deny'));
  }, []);

  if (state === 'loading') {
    return <div className="p-8 text-gray-400">Проверка доступа…</div>;
  }
  if (state === 'deny') {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

export default App; 